/**
 * pm-gallery.js — Product media gallery (Tier 1 interactions)
 * IIFE pattern per Slice 4 LOCKED #18. Direct query-and-bind, no custom element.
 * Dependencies: none. Loaded always alongside _product-media.
 *
 * Phase 2.4 scope: thumb/arrow/dot/keyboard nav, scroll-snap sync via
 * IntersectionObserver, product:variant-changed LISTENER only.
 * Dispatcher for variant sync lands with the variant picker (Phase 2.5+),
 * where dual-fire of both product:variant-changed and variant:changed
 * keeps section-sticky-atc in sync during the transition.
 */

(function () {
  'use strict';

  var GUARD = '__pmGalleryInit';

  function init(root) {
    if (!root || root[GUARD]) return;

    var mainEl = root.querySelector('[data-pm-main]');
    if (!mainEl) return;

    var slides = [].slice.call(mainEl.querySelectorAll('.pm-gallery__slide'));
    if (slides.length === 0) return;

    var thumbs = [].slice.call(root.querySelectorAll('[data-pm-thumbs] button'));
    var dots = [].slice.call(root.querySelectorAll('[data-pm-dot]'));
    var prevBtn = root.querySelector('[data-pm-nav="prev"]');
    var nextBtn = root.querySelector('[data-pm-nav="next"]');
    var counterEl = root.querySelector('[data-pm-counter-current]');
    var total = slides.length;

    root._pmIndex = 0;

    function setActive(index, options) {
      options = options || {};
      if (index < 0 || index >= total) return;
      var prev = root._pmIndex;
      if (prev === index && !options.force) return;

      if (slides[prev]) slides[prev].classList.remove('pm-gallery__slide--active');
      slides[index].classList.add('pm-gallery__slide--active');

      thumbs.forEach(function (t, i) {
        var active = i === index;
        t.classList.toggle('pm-gallery__thumb--active', active);
        t.setAttribute('aria-current', active ? 'true' : 'false');
      });

      dots.forEach(function (d, i) {
        d.classList.toggle('pm-gallery__dot--active', i === index);
      });

      if (counterEl && !options.skipCounter) {
        counterEl.textContent = String(index + 1);
      }

      if (!options.skipScroll && mainEl.scrollWidth > mainEl.clientWidth + 1) {
        mainEl.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
      }

      root._pmIndex = index;
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { setActive(i); });
    });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var i = parseInt(dot.getAttribute('data-pm-dot'), 10);
        if (!isNaN(i)) setActive(i);
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        setActive((root._pmIndex - 1 + total) % total);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        setActive((root._pmIndex + 1) % total);
      });
    }

    function onKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActive((root._pmIndex - 1 + total) % total);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActive((root._pmIndex + 1) % total);
      }
    }
    root.addEventListener('keydown', onKeydown);
    root._pmKeyHandler = onKeydown;

    if ('IntersectionObserver' in window && mainEl.scrollWidth > mainEl.clientWidth + 1) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            var idx = slides.indexOf(entry.target);
            if (idx !== -1 && idx !== root._pmIndex) {
              setActive(idx, { skipScroll: true });
            }
          }
        });
      }, { root: mainEl, threshold: 0.5 });

      slides.forEach(function (s) { observer.observe(s); });
      root._pmObserver = observer;
    }

    function onVariantChange(e) {
      var variant = e.detail && e.detail.variant;
      if (!variant || !variant.id) return;

      if (variant.featured_media && variant.featured_media.id) {
        var mediaId = String(variant.featured_media.id);
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-media-id') === mediaId) {
            setActive(i);
            return;
          }
        }
      }

      var target = String(variant.id);
      for (var j = 0; j < slides.length; j++) {
        var ids = slides[j].getAttribute('data-variant-ids');
        if (!ids) continue;
        if (ids.split(/\s+/).indexOf(target) !== -1) {
          setActive(j);
          return;
        }
      }
    }
    document.addEventListener('product:variant-changed', onVariantChange);
    root._pmVariantHandler = onVariantChange;

    root.setAttribute('data-pm-js', '');
    setActive(0, { force: true, skipScroll: true });

    var zoomBehavior = root.getAttribute('data-zoom-behavior');
    if (zoomBehavior && zoomBehavior !== 'none') {
      setupZoomLoader(root, zoomBehavior);
    }

    root[GUARD] = true;
  }

  function setupZoomLoader(root, behavior) {
    var loaded = false;
    var mainEl = root.querySelector('[data-pm-main]');
    if (!mainEl) return;

    function loadModule(initialIndex) {
      if (loaded) return;
      loaded = true;

      var moduleName = behavior === 'click_lightbox' ? 'pm-lightbox' : 'pm-hover-zoom';
      var jsUrl = root.getAttribute('data-asset-url-' + moduleName);
      var cssUrl = root.getAttribute('data-asset-url-' + moduleName + '-css');
      if (!jsUrl || !cssUrl) return;

      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      document.head.appendChild(link);

      var script = document.createElement('script');
      script.src = jsUrl;
      script.onload = function () {
        var mod = behavior === 'click_lightbox' ? window.__pmLightbox : window.__pmHoverZoom;
        if (!mod || typeof mod.init !== 'function') return;
        mod.init(root);
        if (behavior === 'click_lightbox' && typeof initialIndex === 'number' && typeof mod.open === 'function') {
          mod.open(root, initialIndex);
        }
      };
      document.head.appendChild(script);
    }

    if (behavior === 'click_lightbox') {
      mainEl.addEventListener('click', function (e) {
        if (e.target.closest('[data-pm-nav]')) return;
        if (e.target.closest('[data-pm-dot]')) return;
        if (e.target.closest('video')) return;
        var slide = e.target.closest('.pm-gallery__slide');
        var idx = typeof root._pmIndex === 'number' ? root._pmIndex : 0;
        if (slide) {
          var list = mainEl.querySelectorAll('.pm-gallery__slide');
          for (var i = 0; i < list.length; i++) {
            if (list[i] === slide) { idx = i; break; }
          }
        }
        loadModule(idx);
      }, { once: true });
    } else if (behavior === 'hover_zoom_in_place') {
      var trigger = function () {
        loadModule();
        mainEl.removeEventListener('mouseenter', trigger);
        mainEl.removeEventListener('touchstart', trigger);
      };
      mainEl.addEventListener('mouseenter', trigger, { once: true });
      mainEl.addEventListener('touchstart', trigger, { once: true, passive: true });
    }
  }

  function teardown(root) {
    if (!root || !root[GUARD]) return;
    if (root._pmObserver) {
      root._pmObserver.disconnect();
      root._pmObserver = null;
    }
    if (root._pmVariantHandler) {
      document.removeEventListener('product:variant-changed', root._pmVariantHandler);
      root._pmVariantHandler = null;
    }
    if (root._pmKeyHandler) {
      root.removeEventListener('keydown', root._pmKeyHandler);
      root._pmKeyHandler = null;
    }
    root.removeAttribute('data-pm-js');
    delete root[GUARD];
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pm-gallery]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-pm-gallery]').forEach(teardown);
  });
})();
